import React, { useState } from "react";
import { Bell, X, AlarmClock } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useI18n } from "@/context/I18nContext";

export default function AlarmPanel({ onClose }) {
  const { current, alarm, setAlarm, cancelAlarm } = usePlayer();
  const { t } = useI18n();
  const [time, setTime] = useState(alarm?.time || "07:30");

  const canSet = !!current;

  const handleSet = () => {
    if (!current) return;
    setAlarm({ time, station: current, enabled: true });
    onClose();
  };

  return (
    <div
      data-testid="alarm-panel"
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md glass border border-white/10 p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlarmClock size={16} className="text-pink-400" />
          <div>
            <div className="font-heading font-black text-base tracking-tight">{t("alarm.title").toUpperCase()}</div>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">{t("alarm.subtitle")}</div>
          </div>
        </div>
        <button
          data-testid="alarm-close"
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-white border border-[var(--voix-border)]"
        >
          <X size={14} />
        </button>
      </div>

      {alarm?.enabled && (
        <div data-testid="alarm-active" className="mb-4 border border-pink-500/40 bg-pink-500/5 p-3">
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-pink-400">{t("alarm.next").toUpperCase()}</div>
          <div className="font-heading font-bold text-2xl mt-1">{alarm.time}</div>
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 mt-1 truncate">
            → {alarm.station?.name}
          </div>
          <button
            data-testid="alarm-cancel"
            onClick={cancelAlarm}
            className="mt-2 text-[10px] font-mono-tech uppercase tracking-widest text-pink-400 hover:text-pink-300"
          >
            {t("alarm.cancel")}
          </button>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="block font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t("alarm.time")}</label>
          <input
            data-testid="alarm-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full bg-black/50 border border-[var(--voix-border)] px-3 py-2.5 font-heading font-black text-2xl tracking-tight outline-none focus:border-pink-500/60"
          />
        </div>

        <div>
          <label className="block font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t("alarm.station")}</label>
          <div className="border border-[var(--voix-border)] bg-black/50 px-3 py-2.5">
            {current ? (
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold text-sm truncate">{current.name}</div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 truncate">
                    {t("alarm.station.current")} · {current.country}
                  </div>
                </div>
              </div>
            ) : (
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 py-1">
                {t("alarm.station.pick_first")}
              </div>
            )}
          </div>
        </div>

        <button
          data-testid="alarm-set"
          onClick={handleSet}
          disabled={!canSet}
          className={`w-full py-3 border font-mono-tech text-[11px] uppercase tracking-widest transition ${
            canSet
              ? "border-pink-500 text-pink-300 hover:bg-pink-500/10"
              : "border-[var(--voix-border)] text-slate-600 cursor-not-allowed"
          }`}
        >
          <Bell size={12} className="inline mr-2" />
          {t("alarm.set")}
        </button>
      </div>
    </div>
  );
}
